/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Icons } from '@sede-enterprise/shell-ds-react-framework';
import type { ISelectProps } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Select/Select.types';
import { FormProvider, useForm } from 'react-hook-form';
import FormControlDatePicker from 'src/UI/FormComponent/FormControlDatePicker';
import FormControlSelect from 'src/UI/FormComponent/FormControlSelect';
import FormControlTextInput from 'src/UI/FormComponent/FormControlTextInput';
import { MAX_LENGTH_50, MESSAGES } from 'src/constants/EUMarkets';
import Button from 'src/UI/Button';
import { ISelectOptions } from 'src/types/Common.type';
import { IMarketBalance } from 'src/types/EUMarkets.type';
import { formatDateOnly, isDateRangeValid } from '../../Locations/AddEditParameter';

interface IAddEditBalanceProps {
  open: boolean;
  marketId: number;
  balance: IMarketBalance | null;
  organizations: ISelectOptions[];
  loadingOrganizations: boolean;
  marketValidFrom?: Dayjs | null;
  marketValidTo?: Dayjs | null;
  onClose: () => void;
  onDraftChange: (balance: IMarketBalance, error?: string) => void;
}

// Balance dates must sit inside the parent market window.
export const getBalanceDraftError = (
  values,
  marketValidFrom?: Dayjs | null,
  marketValidTo?: Dayjs | null,
) => {
  if (!String(values.balanceName ?? '').trim()) {
    return MESSAGES.REQUIRED_BALANCE_NAME;
  }

  if (String(values.balanceName).length > MAX_LENGTH_50) {
    return MESSAGES.MAX_LENGTH_50;
  }

  if (!Number(values.organizationId)) {
    return MESSAGES.REQUIRED_INTERNAL_ENTITY;
  }

  if (!values.validFrom) {
    return MESSAGES.REQUIRED_VALID_FROM;
  }

  // Valid To is optional - an empty end date means the balance is open ended.
  if (values.validTo && !isDateRangeValid(values.validFrom, values.validTo)) {
    return MESSAGES.INVALID_PARAMETER_DATE_RANGE;
  }

  const validFrom = dayjs(values.validFrom);

  if (marketValidFrom?.isValid() && validFrom.isBefore(marketValidFrom, 'day')) {
    return MESSAGES.BALANCE_DATE_OUTSIDE_MARKET_RANGE;
  }

  if (
    values.validTo &&
    marketValidTo?.isValid() &&
    dayjs(values.validTo).isAfter(marketValidTo, 'day')
  ) {
    return MESSAGES.BALANCE_DATE_OUTSIDE_MARKET_RANGE;
  }

  return undefined;
};

const { Trash } = Icons;

const AddEditBalance: FC<IAddEditBalanceProps> = ({
  open,
  marketId,
  balance,
  organizations,
  loadingOrganizations,
  marketValidFrom,
  marketValidTo,
  onClose,
  onDraftChange,
}) => {
  const methods = useForm();
  const { reset, setValue, watch } = methods;
  const validFrom = watch('validFrom');
  const validTo = watch('validTo');
  // The parent rebuilds these dayjs instances on every render, so depend on their value rather
  // than their identity; otherwise the reset below re-fires continuously and wipes user input.
  const marketValidFromKey = marketValidFrom?.isValid() ? marketValidFrom.format('YYYY-MM-DD') : '';
  const marketValidToKey = marketValidTo?.isValid() ? marketValidTo.format('YYYY-MM-DD') : '';

  useEffect(() => {
    if (!open) {
      return;
    }
    // New rows inherit the market validity window; existing rows keep their stored dates.
    reset({
      balanceName: balance?.balanceName ?? '',
      organizationId: balance?.organizationId ?? null,
      validFrom: balance?.validFrom
        ? dayjs(balance.validFrom)
        : (marketValidFromKey && dayjs(marketValidFromKey)) || dayjs(),
      validTo: balance?.validTo
        ? dayjs(balance.validTo)
        : (marketValidToKey && dayjs(marketValidToKey)) || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, balance, marketValidFromKey, marketValidToKey]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const subscription = methods.watch((values) => {
      onDraftChange(
        {
          id: balance?.id ?? 0,
          marketId,
          balanceName: String(values.balanceName ?? '').trim(),
          organizationId: Number(values.organizationId) || 0,
          organizationName:
            organizations.find((option) => String(option.id) === String(values.organizationId))
              ?.name ??
            balance?.organizationName ??
            '',
          validFrom: values.validFrom ? formatDateOnly(values.validFrom as Dayjs) : '',
          validTo: values.validTo ? formatDateOnly(values.validTo as Dayjs) : null,
        },
        getBalanceDraftError(values, marketValidFrom, marketValidTo),
      );
    });

    return () => subscription?.unsubscribe?.();
  }, [
    open,
    balance,
    marketId,
    organizations,
    marketValidFrom,
    marketValidTo,
    onDraftChange,
    methods,
  ]);

  const handleValidFromChange = (newValidFrom: Dayjs | null) => {
    if (newValidFrom && validTo && dayjs(newValidFrom).isAfter(dayjs(validTo), 'day')) {
      setValue('validTo', null, { shouldValidate: true });
    }
  };

  const isBeforeMarketStart = (current: Dayjs) =>
    Boolean(marketValidFrom?.isValid() && current.isBefore(marketValidFrom, 'day'));

  const isAfterMarketEnd = (current: Dayjs) =>
    Boolean(marketValidTo?.isValid() && current.isAfter(marketValidTo, 'day'));

  const validateWithinMarketRange = (value) => {
    if (!value) {
      return true;
    }
    const date = dayjs(value);
    if (marketValidFrom?.isValid() && date.isBefore(marketValidFrom, 'day')) {
      return MESSAGES.BALANCE_DATE_OUTSIDE_MARKET_RANGE;
    }
    if (marketValidTo?.isValid() && date.isAfter(marketValidTo, 'day')) {
      return MESSAGES.BALANCE_DATE_OUTSIDE_MARKET_RANGE;
    }
    return true;
  };

  // Returning the chained expression directly would collapse a message into `true`/`false`,
  // so surface the first failing rule instead.
  const validateBalanceDate = (value, otherDate, isStart: boolean) => {
    const from = isStart ? value : otherDate;
    const to = isStart ? otherDate : value;
    if (from && to && !isDateRangeValid(from, to)) {
      return MESSAGES.INVALID_PARAMETER_DATE_RANGE;
    }
    return validateWithinMarketRange(value);
  };

  if (!open) {
    return null;
  }

  return (
    <div className='parameter-inline-editor'>
      <FormProvider {...methods}>
        <div className='parameter-inline-fields'>
          <div className='parameter-inline-cell'>
            <FormControlTextInput
              methods={methods}
              label='Balance'
              controllerProps={{
                name: 'balanceName',
                rules: {
                  required: MESSAGES.REQUIRED_BALANCE_NAME,
                  maxLength: { value: MAX_LENGTH_50, message: MESSAGES.MAX_LENGTH_50 },
                },
              }}
              inputProps={{ placeholder: 'Enter Balance' }}
            />
          </div>
          <div className='parameter-inline-cell'>
            <FormControlSelect
              methods={methods}
              label='Internal Entity'
              isLoading={loadingOrganizations}
              data={organizations}
              controllerProps={{
                name: 'organizationId',
                rules: { required: MESSAGES.REQUIRED_INTERNAL_ENTITY },
              }}
              selectProps={{
                className: 'balance-entity-select',
                placeholder: 'Select',
                getPopupContainer: (triggerNode) =>
                  (triggerNode?.closest('.shell-modal-container') as HTMLElement) ?? document.body,
                placement: 'bottomLeft' as ISelectProps['placement'],
              }}
            />
          </div>
          <div className='parameter-inline-cell'>
            <FormControlDatePicker
              methods={methods}
              label='Valid From'
              controllerProps={{
                name: 'validFrom',
                rules: {
                  required: MESSAGES.REQUIRED_VALID_FROM,
                  validate: (value) => validateBalanceDate(value, validTo, true),
                },
              }}
              handleOnChange={handleValidFromChange}
              inputProps={{
                disabledDate: (current: Dayjs) =>
                  isBeforeMarketStart(current) || isAfterMarketEnd(current),
              }}
            />
          </div>
          <div className='parameter-inline-cell'>
            <FormControlDatePicker
              methods={methods}
              label='Valid To'
              controllerProps={{
                name: 'validTo',
                rules: {
                  validate: (value) => validateBalanceDate(value, validFrom, false),
                },
              }}
              inputProps={{
                disabledDate: (current: Dayjs) =>
                  isBeforeMarketStart(current) ||
                  isAfterMarketEnd(current) ||
                  Boolean(validFrom && current.isBefore(validFrom, 'day')),
              }}
            />
          </div>
        </div>
      </FormProvider>
      {!balance && (
        <div className='parameter-inline-actions'>
          <Button
            type='button'
            size='small'
            variant='transparent'
            icon={<Trash />}
            iconOnly
            aria-label='Remove new balance'
            title='Remove new balance'
            data-testid='remove-new-balance'
            onClick={onClose}
          />
        </div>
      )}
    </div>
  );
};

export default AddEditBalance;
